import React from 'react'
import SimpleMarkdown from 'simple-markdown'
import {Link} from 'react-router-dom'
import merge from 'lodash-es/merge'
import injectSheet from 'react-jss'
import ScrollableAnchor from 'react-scrollable-anchor'
import styles from './styles'

const LIST_BULLET = '(?:[*+-]|\\d+\\.)';
const LIST_R = new RegExp(
	`^(\n? *)(${LIST_BULLET}) ` +
	'[\\s\\S]+?(?:\n{2,}(?! )' +
	`(?!\\1${LIST_BULLET} )\\n*` +
	'|\\s*\n*$)'
);
const LIST_LOOKBEHIND_R = /^$|\n *$/;
const LINK_INSIDE = '(?:\\[[^\\]]*\\]|[^\\[\\]]|\\](?=[^\\[]*\\]))*';
const LINK_HREF_AND_TITLE = '\\s*<?((?:[^\\s\\\\]|\\\\.)*?)>?(?:\\s+[\'"]([\\s\\S]*?)[\'"])?\\s*';

const rules = merge({}, SimpleMarkdown.defaultRules, {
	list: {
		match: function(source, state, prevCapture) {
			const isStartOfLine = LIST_LOOKBEHIND_R.test(prevCapture);
			const isListBlock = state._list || !state.inline;
			const nested = /^[^\n]*$/.test(prevCapture) && /^\n/.test(source);

			if ((isStartOfLine || nested) && isListBlock) {
				return LIST_R.exec(source);
			} else {
				return null;
			}
		}
	},
	link: {
		react: function (node, output, state) { // eslint-disable-line react/display-name
			const url = SimpleMarkdown.sanitizeUrl(node.target);
			if (url.match(window.location.host) && url.match(`^(https?://)?(${window.location.host})(.*)$`)) {
				return <Link
					key={state.key}
					to={{pathname: url.match(`^(https?://)?(${window.location.host})(.*)$`)[3]}}
					title={node.title}
				>{output(node.content, state)}</Link>;
			} else {
				return <a href={url} key={state.key} title={node.title}>{output(node.content, state)}</a>;
			}
		},
	},
	text: {
		match: SimpleMarkdown.inlineRegex(
			/^[\s\S]+?(?=[^0-9A-Za-z,.\-&@:;\s\u00c0-\uffff]|\n| {2,}\n|\w+:\S|$)/
		),
		react: function (node, output, state) {
			const items = node.content.split('\n');
			return items.map((text, index) => index === items.length - 1 ? text : [text, <br key={index}/>]);
		}
	},
	image: {
		match: SimpleMarkdown.inlineRegex(new RegExp(
			`^!\\[(${LINK_INSIDE})\\]\\(${LINK_HREF_AND_TITLE}\\)( *{ *(width: *(\\d+))?,? *(height: *(\\d+))? *})?`
		)),
		parse: function(capture, parse, state) {
			const image = {
				alt: capture[1],
				target: SimpleMarkdown.unescapeUrl(capture[2]),
				title: capture[3],
				width: capture[6],
				height: capture[8]
			};
			return image;
		},
		react: function(node, output, state) { // eslint-disable-line react/display-name
			const src = SimpleMarkdown.sanitizeUrl(node.target);
			return <img key={state.key} src={src} alt={node.alt} title={node.title} width={node.width} height={node.height}/>
		}
	},
	anchor: {
		match: SimpleMarkdown.anyScopeRegex(/^{#([a-zA-Z-_]*)} */),
		parse: function(capture, parse, state) {
			return {id: capture[1]};
		},
		react: function (node, output, state) { // eslint-disable-line react/display-name
			return <ScrollableAnchor key={state.key} id={node.id}><div/></ScrollableAnchor>;
		},
		order: 0
	}
});

const Markdown = ({classes, text, errorHandler, children}) => {
	try {
		const blockSource = text + '\n\n';
		const tree = SimpleMarkdown.parserFor(rules)(blockSource, { inline: false });
		return (
			<div className={classes.markdown}>
				{SimpleMarkdown.reactFor(SimpleMarkdown.ruleOutput(rules, 'react'))(tree)}
			</div>
		);
	} catch (errors) {
		errorHandler ? errorHandler(errors, children) : console.error(errors); // eslint-disable-line no-console
		return null;
	}
};

export default injectSheet(styles)(Markdown);
